import type { EffectStates } from '../config/effects';
import { EFFECTS } from '../config/effects';
import { applyParams, rewire } from '../audio/effects-chain';
import { ensureChain } from '../audio/engine';
import * as store from '../state/store';
import { effectLabel, paramLabel, t } from '../i18n';

const formatValue = (v: number, step: number): string => (step < 1 ? v.toFixed(2) : String(Math.round(v)));

function miniSlider(
  effectKey: string,
  paramKey: string,
  label: string,
  min: number,
  max: number,
  step: number,
  states: EffectStates,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'mini-row';

  const name = document.createElement('span');
  name.className = 'mini-label';
  name.textContent = label;

  const value = (states[effectKey]?.[paramKey] as number | undefined) ?? 0;

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.className = 'mini-slider';
  slider.min = String(min);
  slider.max = String(max);
  slider.step = String(step);
  slider.value = String(value);
  slider.setAttribute('aria-label', `${effectKey} ${label}`);

  const readout = document.createElement('span');
  readout.className = 'mini-label';
  readout.textContent = formatValue(value, step);

  slider.addEventListener('input', () => {
    const v = Number(slider.value);
    const state = states[effectKey];
    if (!state) return;
    state[paramKey] = v;
    readout.textContent = formatValue(v, step);
    applyParams(effectKey, state); // no-op while the effect is switched off
    store.touch('effects');
  });

  row.append(name, slider, readout);
  return row;
}

export interface EffectsUiHandles {
  render: () => void;
}

export function createEffectsUi(container: HTMLElement, notice: (text: string, kind: 'err' | '') => void): EffectsUiHandles {
  const render = (): void => {
    const states = store.get('effects');
    const frag = document.createDocumentFragment();

    for (const def of EFFECTS) {
      const state = states[def.key];
      if (!state) continue;

      const wrap = document.createElement('div');
      wrap.className = `field effect-field${state.enabled ? ' on' : ''}`;

      const checkRow = document.createElement('div');
      checkRow.className = 'checkbox-row';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.id = `fx_${def.key}_on`;
      cb.checked = state.enabled;
      const label = document.createElement('label');
      label.htmlFor = cb.id;
      label.textContent = effectLabel(def.key);
      checkRow.append(cb, label);
      wrap.appendChild(checkRow);

      if (def.hasWet) wrap.appendChild(miniSlider(def.key, 'wet', t('effect.mix'), 0, 1, 0.01, states));
      for (const p of def.params) {
        wrap.appendChild(miniSlider(def.key, p.key, paramLabel(p.key), p.min, p.max, p.step, states));
      }

      cb.addEventListener('change', () => {
        state.enabled = cb.checked;
        // Build the graph first, otherwise rewire has nothing to wire into and
        // an unsupported effect would appear to have been accepted.
        ensureChain(store.getSettings());
        const { rejected } = rewire(states);
        if (rejected.includes(def.key)) {
          cb.checked = false;
          notice(t('worklet.hint'), 'err');
        } else if (cb.checked) {
          notice('', '');
        }
        wrap.classList.toggle('on', cb.checked);
        store.touch('effects');
      });

      frag.appendChild(wrap);
    }

    container.replaceChildren(frag);
  };

  render();
  return { render };
}
